import { AlertProvider } from './Molecules/Alert.js';
import { ConfirmProvider } from './Molecules/Confirm.js';
import React__default, { createContext, useEffect } from 'react';
import ColorUtil from './Utils/color.util.js';
import CurrencyUtil from './Utils/currency.util.js';
import { ToastProvider } from './Molecules/Toast.js';
import { createFontCSSVariables } from './Context/FontConfig.js';
import { createThemeCSSVariables } from './Context/ThemeConfig.js';

var defaultTheme = 'Luna';
var defaultThemes = {
    Luna: {
        fontFamily: 'Open Sans',
        currency: 'INR',
        countryCode: '+91',
        colors: {
            warningColor: '#C69A2B',
            headerBackgroundColor: '#ffffff',
            primaryColor: '#2C016C',
            secondaryColor: '#FFF',
            tertiaryColor: '#FACD5D',
            successColor: '#419D3E',
            failureColor: '#FF0000',
            primaryTextColor: '#182135',
            secondaryTextColor: '#626264'
        }
    },
    Aurelia: {
        fontFamily: 'Sora',
        currency: 'INR',
        countryCode: '+91',
        type: 'dark',
        colors: {
            warningColor: '#C69A2B',
            headerBackgroundColor: '#ffffff',
            primaryColor: '#FACD5D',
            secondaryColor: '#FFF',
            tertiaryColor: '#FACD5D',
            successColor: '#419D3E',
            failureColor: '#FF0000',
            primaryTextColor: '#182135',
            secondaryTextColor: '#626264'
        }
    }
};
var ThemeContext = createContext({
    theme: defaultThemes[defaultTheme]
});
function ThemeProvider(_a) {
    var theme = _a.theme, children = _a.children;
    useEffect(function () {
        createCssVariablesDynamically(theme);
        viewportHeightForMobile();
        CurrencyUtil.setCurrency((theme === null || theme === void 0 ? void 0 : theme.currency) || 'INR');
    }, [theme]);
    return (React__default.createElement(ThemeContext.Provider, { value: { theme: theme } },
        React__default.createElement(ToastProvider, null,
            React__default.createElement(ConfirmProvider, null,
                React__default.createElement(AlertProvider, null, children)))));
}
var createCssVariablesDynamically = function (theme) {
    if (typeof window !== 'undefined') {
        var root = document.documentElement;
        var primaryColor = theme === null || theme === void 0 ? void 0 : theme.colors.primaryColor;
        var primaryColor95 = ColorUtil.increaseBrightness(primaryColor, 95) || primaryColor;
        var primaryColor90 = ColorUtil.increaseBrightness(primaryColor, 90) || primaryColor;
        var primaryColor80 = ColorUtil.increaseBrightness(primaryColor, 80) || primaryColor;
        var primaryColor60 = ColorUtil.increaseBrightness(primaryColor, 60) || primaryColor;
        var primaryColor10 = ColorUtil.increaseBrightness(primaryColor, 10) || primaryColor;
        var primaryColorDark60 = ColorUtil.decreaseBrightness(primaryColor, 60) || primaryColor;
        // Misc colors
        var secondaryColor = theme === null || theme === void 0 ? void 0 : theme.colors.secondaryColor;
        var tertiaryColor = theme === null || theme === void 0 ? void 0 : theme.colors.tertiaryColor;
        var successColor = theme === null || theme === void 0 ? void 0 : theme.colors.successColor;
        var failureColor = theme === null || theme === void 0 ? void 0 : theme.colors.failureColor;
        var primaryTextColor = theme === null || theme === void 0 ? void 0 : theme.colors.primaryTextColor;
        var secondaryTextColor = theme === null || theme === void 0 ? void 0 : theme.colors.secondaryTextColor;
        var headerBackgroundColor = theme === null || theme === void 0 ? void 0 : theme.colors.headerBackgroundColor;
        var fontFamily = theme === null || theme === void 0 ? void 0 : theme.fontFamily;
        root === null || root === void 0 ? void 0 : root.style.setProperty('--primary-color', primaryColor, '');
        root === null || root === void 0 ? void 0 : root.style.setProperty('--primary-color-95', primaryColor95, '');
        root === null || root === void 0 ? void 0 : root.style.setProperty('--primary-color-90', primaryColor90, '');
        root === null || root === void 0 ? void 0 : root.style.setProperty('--primary-color-80', primaryColor80, '');
        root === null || root === void 0 ? void 0 : root.style.setProperty('--primary-color-60', primaryColor60, '');
        root === null || root === void 0 ? void 0 : root.style.setProperty('--primary-color-10', primaryColor10, '');
        root === null || root === void 0 ? void 0 : root.style.setProperty('--primary-color-dark-60', primaryColorDark60, '');
        if (secondaryColor) {
            root === null || root === void 0 ? void 0 : root.style.setProperty('--secondary-color', secondaryColor, '');
        }
        if (tertiaryColor) {
            root === null || root === void 0 ? void 0 : root.style.setProperty('--tertiary-color', tertiaryColor, '');
        }
        if (secondaryTextColor) {
            root === null || root === void 0 ? void 0 : root.style.setProperty('--secondary-text-color', secondaryTextColor, '');
        }
        if (headerBackgroundColor) {
            root === null || root === void 0 ? void 0 : root.style.setProperty('--header-background-color', headerBackgroundColor, '');
        }
        if (fontFamily) {
            root === null || root === void 0 ? void 0 : root.style.setProperty('--font-family', fontFamily, '');
            createFontCSSVariables(fontFamily);
        }
        if (theme === null || theme === void 0 ? void 0 : theme.type) {
            createThemeCSSVariables(theme === null || theme === void 0 ? void 0 : theme.type);
        }
        if (successColor) {
            root === null || root === void 0 ? void 0 : root.style.setProperty('--success-color', successColor, '');
        }
        if (failureColor) {
            root === null || root === void 0 ? void 0 : root.style.setProperty('--failure-color', failureColor, '');
        }
        if (primaryTextColor) {
            root === null || root === void 0 ? void 0 : root.style.setProperty('--primary-text-color', primaryTextColor, '');
        }
        if (secondaryTextColor) {
            root === null || root === void 0 ? void 0 : root.style.setProperty('--secondary-text-color', secondaryTextColor, '');
        }
    }
};
var viewportHeightForMobile = function () {
    if (typeof window !== 'undefined') {
        var vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', "".concat(vh, "px"));
        window.addEventListener('resize', function () {
            var vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', "".concat(vh, "px"));
        });
    }
};

export { ThemeContext, ThemeProvider as default, defaultTheme, defaultThemes };
//# sourceMappingURL=provider.js.map
